import { capitalize } from 'lodash';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { RangeInput } from 'shared/components/FormControl';
import { FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import { fontImportUrl } from 'dashboard/components/ThemedFonts/ThemedFonts';
import ThemeEditorFormActionFooter, {
  ACTION_FOOTER_HEIGHT,
} from 'dashboard/components/ThemeEditorFormActionFooter';
import { FORM_THEME_EDITOR_FONT_NAME } from 'dashboard/lib/constants/formNames';
import { COLORS } from 'dashboard/styles/variables';
import useExternalStylesheet from 'lib/hooks/useExternalStylesheet';
import { BrowserFonts, GoogleFonts } from 'lib/interfaces/fonts';
import { FontWeight, TextAlign } from 'lib/interfaces/ThemeSerialized';
import { ThemeAttributes } from 'verify/models/theme';

const Title = styled('h2')`
  color: ${COLORS.black};

  padding-top: ${SPACINGS.base * 5}px;
  padding-left: ${SPACINGS.base * 2}px;
  padding-bottom: ${SPACINGS.base * 3}px;
  border-bottom: 1px solid ${COLORS.gray20};
`;
const Section = styled('div')`
  padding: ${SPACINGS.base * 2}px;
  margin-bottom: ${ACTION_FOOTER_HEIGHT};
`;
const StyledLabel = styled('label')`
  display: inline-block;
`;
const Hint = styled('p')`
  font-size: ${FONT_SIZES.small};
  color: ${COLORS.gray50};
  margin-bottom: ${SPACINGS.base}px;
`;
const StyledField = styled(Field)<any>`
  margin-bottom: ${SPACINGS.base * 3}px;
`;

interface ThemeFormData {
  theme: ThemeAttributes;
}

interface FormThemeEditorFontProps {
  disabled?: boolean;
}

const ALL_FONTS: { [k: string]: string } = { ...BrowserFonts, ...GoogleFonts };

const FormThemeEditorFont = ({
  initialValues,
  handleSubmit,
  disabled,
  reset,
  submitting,
  pristine,
}: FormThemeEditorFontProps & InjectedFormProps<ThemeFormData, {}>) => {
  // NOTE: we are calling a hook on a loop on a constant value, so we'll never
  // break the rules of hooks.
  Object.values(GoogleFonts).map((fontFamily) => useExternalStylesheet(fontImportUrl(fontFamily))); // eslint-disable-line react-hooks/rules-of-hooks

  const allFonts = { ...ALL_FONTS };
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const hasCustomFont = initialValues?.['theme']['font-url'] != null;
  if (hasCustomFont) {
    const customFont =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      initialValues['theme']['font-family'] ?? initialValues['theme']['font-family-title'];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    allFonts[customFont] = customFont;
  }
  const allFontsOptions = Object.keys(allFonts)
    .filter(Boolean)
    .sort((a, b) => allFonts[a].localeCompare(allFonts[b]))
    .map((fontKey) => ({
      label: <div style={{ fontFamily: allFonts[fontKey] }}>{allFonts[fontKey]}</div>,
      value: allFonts[fontKey],
    }));

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Title>Fonts</Title>
        <Section>
          <StyledLabel>Title font</StyledLabel>
          <Hint>Font for titles</Hint>
          <StyledField
            id='theme.font-family-title'
            name='theme.font-family-title'
            component={ReactSelectField}
            disabled={hasCustomFont}
            options={allFontsOptions}
          />

          <StyledLabel htmlFor='theme.font-size-header'>Title font size</StyledLabel>
          <Hint>Font size for titles</Hint>
          <StyledField
            id='theme.font-size-header'
            name='theme.font-size-header'
            component={RangeInput}
            min={14}
            max={36}
            unit='px'
            format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
            normalize={(value: number) => `${value}px`}
          />

          <StyledLabel htmlFor='theme.header-font-weight'>Title font weight</StyledLabel>
          <Hint>Font weight for titles</Hint>
          <StyledField
            id='theme.header-font-weight'
            name='theme.header-font-weight'
            component={SelectField}
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            {Object.keys(FontWeight).map((fontWeightKey: keyof typeof FontWeight) => (
              <Option key={fontWeightKey} value={FontWeight[fontWeightKey]}>
                {FontWeight[fontWeightKey]}
              </Option>
            ))}
          </StyledField>

          <StyledLabel htmlFor='theme.line-height-header'>Title line height</StyledLabel>
          <Hint>Line height for titles</Hint>
          <StyledField
            id='theme.line-height-header'
            name='theme.line-height-header'
            component={RangeInput}
            min={14}
            max={54}
            unit='px'
            format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
            normalize={(value: number) => `${value}px`}
          />

          <StyledLabel>Body font</StyledLabel>
          <Hint>Font for body text and buttons</Hint>
          <StyledField
            id='theme.font-family'
            name='theme.font-family'
            component={ReactSelectField}
            disabled={hasCustomFont}
            options={allFontsOptions}
          />

          <StyledLabel htmlFor='theme.font-size-body'>Body font size</StyledLabel>
          <Hint>Font size for body text</Hint>
          <StyledField
            id='theme.font-size-body'
            name='theme.font-size-body'
            component={RangeInput}
            min={12}
            max={22}
            unit='px'
            format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
            normalize={(value: number) => `${value}px`}
          />

          <StyledLabel htmlFor='theme.line-height-body'>Body line height</StyledLabel>
          <Hint>Line height for body text</Hint>
          <StyledField
            id='theme.line-height-body'
            name='theme.line-height-body'
            component={RangeInput}
            min={12}
            max={32}
            unit='px'
            format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
            normalize={(value: number) => `${value}px`}
          />

          <StyledLabel htmlFor='theme.font-size-small'>Small text font size</StyledLabel>
          <Hint>Font size for small text</Hint>
          <StyledField
            id='theme.font-size-small'
            name='theme.font-size-small'
            component={RangeInput}
            min={10}
            max={16}
            unit='px'
            format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
            normalize={(value: number) => `${value}px`}
          />

          <StyledLabel htmlFor='theme.line-height-small'>Small text line height</StyledLabel>
          <Hint>Line height for small text</Hint>
          <StyledField
            id='theme.line-height-small'
            name='theme.line-height-small'
            component={RangeInput}
            min={10}
            max={24}
            unit='px'
            format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
            normalize={(value: number) => `${value}px`}
          />

          <StyledLabel htmlFor='theme.button-font-weight'>Button font weight</StyledLabel>
          <StyledField
            id='theme.button-font-weight'
            name='theme.button-font-weight'
            component={SelectField}
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            {Object.keys(FontWeight).map((fontWeightKey: keyof typeof FontWeight) => (
              <Option key={fontWeightKey} value={FontWeight[fontWeightKey]}>
                {FontWeight[fontWeightKey]}
              </Option>
            ))}
          </StyledField>

          <StyledLabel htmlFor='theme.text-align'>Text align</StyledLabel>
          <StyledField id='theme.text-align' name='theme.text-align' component={SelectField}>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            {Object.keys(TextAlign).map((textAlignKey: keyof typeof TextAlign) => (
              <Option key={textAlignKey} value={TextAlign[textAlignKey]}>
                {capitalize(TextAlign[textAlignKey])}
              </Option>
            ))}
          </StyledField>
        </Section>
        {!disabled && (
          <ThemeEditorFormActionFooter
            dataTestBlock='form-theme-editor-font'
            handleUndo={reset}
            submitting={submitting}
            pristine={pristine}
          />
        )}
      </form>
    </>
  );
};

export default reduxForm<{}, FormThemeEditorFontProps>({
  form: FORM_THEME_EDITOR_FONT_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormThemeEditorFont);
